/* Value editor: pure model. Validation, hints and the numbers behind the literal-`\n` notice. */
import type { EnvValueType } from '@hangar/shared';
import { isValidJson, literalNewlineCount } from './envHelpers';

interface ValueLike {
  value: string;
  base64?: string;
}

interface ErrorInput {
  /** An error handed in by the parent (server-side); it wins over the local rules. */
  error?: string;
  required?: boolean;
  submitted?: boolean;
  valueType: EnvValueType;
  state: ValueLike;
}

/** Parent error, else "required" once submitted with nothing typed or picked, else live JSON validation. */
export function valueFieldError({ error, required, submitted, valueType, state }: ErrorInput): string | undefined {
  if (error !== undefined) return error;
  if (submitted && required && !state.value && !state.base64) return 'A value is required';
  const jsonInvalid = valueType === 'json' && state.value.trim().length > 0 && !isValidJson(state.value);
  return jsonInvalid ? 'Not valid JSON' : undefined;
}

export const pluralS = (n: number): string => (n === 1 ? '' : 's');

export const realLineCount = (value: string): number => value.split('\n').length;

/** Literal `\n` sequences only matter for text values; files are uploaded byte-exact. */
export const literalCountFor = (valueType: EnvValueType, value: string): number => (valueType !== 'file' ? literalNewlineCount(value) : 0);

export function valueHint(valueType: EnvValueType, realLines: number): string {
  if (valueType === 'multiline') return `${realLines} line${pluralS(realLines)} · real line breaks are kept`;
  if (valueType === 'json') return 'Validated as JSON on save';
  return 'Never printed in logs or previews once saved as sensitive/secret';
}

export const valueRows = (valueType: EnvValueType): number => (valueType === 'multiline' || valueType === 'json' ? 6 : 3);

/** Decoded size of a base64 payload (3 bytes per 4 characters, padding ignored), for display. */
export const base64Bytes = (base64: string | undefined): number => Math.round((base64?.length ?? 0) * 0.75);
